import { useMutation, useQueryClient } from "@tanstack/react-query"
import type { TRadioTrack } from "@/types"

export const feedListKey = ["/radio/tracks", "infinite"]

interface IOptions {
  onSuccess?: () => void
  invalidateOnSettled?: boolean
  onMutate?: (updated: TRadioTrack) => void
}

export function useRadioTrackMutation(
  track: TRadioTrack | null,
  field: "liked" | "is_hidden",
  mutateFn: (id: string) => Promise<unknown>,
  { onSuccess, invalidateOnSettled = true, onMutate }: IOptions = {},
) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: () => (track ? mutateFn(track.id) : Promise.resolve(null)),
    onMutate: async () => {
      if (!track) return

      await queryClient.cancelQueries({ queryKey: feedListKey })

      const previousData = queryClient.getQueriesData({ queryKey: feedListKey })

      const updater = (old: unknown) => {
        const data = old as { pages?: { data: TRadioTrack[] }[] } | undefined
        if (!data?.pages) return old
        return {
          ...data,
          pages: data.pages.map((page) => ({
            ...page,
            data: page.data.map((item) => (item.id === track.id ? { ...item, [field]: !item[field] } : item)),
          })),
        }
      }

      queryClient.setQueriesData({ queryKey: feedListKey }, updater)

      onMutate?.({ ...track, [field]: !track[field] })

      return { previousData }
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        for (const [queryKey, data] of context.previousData) {
          queryClient.setQueryData(queryKey, data)
        }
      }
    },
    onSuccess,
    onSettled: invalidateOnSettled
      ? () => {
          queryClient.invalidateQueries({ queryKey: feedListKey })
        }
      : undefined,
  })
}
